import { useNavigate } from 'react-router-dom';
import { secsData } from '../utils/secsDataLoader';

export default function GemCapabilities() {
  const navigate = useNavigate();
  const gem = secsData.gem;

  const handleMessageClick = (messageId: string) => {
    navigate(`/message/${messageId}`);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">GEM Capability Mapping</h1>
        <p className="text-[var(--color-text-secondary)] max-w-3xl leading-relaxed">
          GEM (Generic Equipment Model, SEMI E30) builds on SECS-II to define a standard set of
          behaviors for equipment-host interaction, including communication establishment, state
          management, event reporting, alarm handling, and recipe management. Below are GEM's core
          state machines and each capability mapped to its actual SECS-II messages for quick reference.
        </p>
      </div>

      {!gem ? (
        <div className="card p-12 text-center text-[var(--color-text-secondary)]">
          GEM data unavailable
        </div>
      ) : (
        <>
          {/* State Models */}
          <section className="mb-10">
            <h2 className="text-xl font-bold mb-4">State Models</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(gem.stateModels ?? []).map((model) => (
                <div key={model.name} className="card p-6">
                  <h3 className="text-lg font-semibold mb-2">{model.name}</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mb-4 leading-relaxed">
                    {model.description}
                  </p>
                  <ol className="space-y-3">
                    {model.states.map((state, index) => (
                      <li key={state.name} className="flex gap-3">
                        <span className="badge badge-primary shrink-0 h-fit font-mono">
                          {index + 1}
                        </span>
                        <div>
                          <div className="font-mono font-semibold text-sm text-primary-600 dark:text-primary-400">
                            {state.name}
                          </div>
                          <div className="text-sm text-[var(--color-text-secondary)]">
                            {state.description}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </section>

          {/* Capabilities */}
          <section>
            <h2 className="text-xl font-bold mb-4">
              Capabilities
              <span className="ml-2 text-sm font-normal text-[var(--color-text-tertiary)]">
                {(gem.capabilities ?? []).length} total
              </span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {(gem.capabilities ?? []).map((capability) => (
                <div key={capability.name} className="card p-6">
                  <h3 className="text-lg font-semibold mb-2 text-[var(--color-text-primary)]">
                    {capability.name}
                  </h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mb-4 leading-relaxed">
                    {capability.description}
                  </p>
                  <div className="pt-4 border-t border-[var(--color-border)]">
                    <div className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase mb-2">
                      Related Messages ({capability.messages.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {capability.messages.map((messageId) => (
                        <button
                          key={messageId}
                          onClick={() => handleMessageClick(messageId)}
                          className="badge badge-primary hover:opacity-80 cursor-pointer font-mono transition-opacity"
                          aria-label={`View message ${messageId}`}
                        >
                          {messageId}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
